import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import { useT } from "../i18n/LanguageContext";

export const Navbar = () => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t("nav.home"), key: "home" },
    { to: "/expertise", label: t("nav.expertise"), key: "expertise" },
    { to: "/modeles", label: t("nav.models"), key: "models" },
    { to: "/pourquoi", label: t("nav.why"), key: "why" },
    { to: "/blog", label: t("nav.blog"), key: "blog" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link">
          <Logo height={40} />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `hx-link-underline text-sm font-medium ${
                  isActive ? "text-[#1f2226]" : "text-[#3f444b] hover:text-[#1f2226]"
                }`
              }
            >
              {({ isActive }) => <span data-active={isActive}>{l.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <LanguageSwitcher />
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className="hx-btn-primary inline-flex items-center gap-2 bg-[#1f2226] text-white px-5 py-2.5 rounded-full text-sm font-medium ml-1"
          >
            {t("nav.contact")}
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <button
          className="lg:hidden p-2 inline-flex items-center gap-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden bg-white border-t border-black/5 px-6 py-6 space-y-4"
          data-testid="nav-mobile-menu"
        >
          {links.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block text-lg ${isActive ? "text-[#1093f0]" : "text-[#1f2226]"}`
              }
              data-testid={`nav-mobile-link-${l.key}`}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="pt-4 border-t border-black/5 flex items-center justify-between">
            <LanguageSwitcher />
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-2 bg-[#1f2226] text-white px-5 py-2.5 rounded-full text-sm font-medium"
              data-testid="nav-mobile-contact-cta"
            >
              {t("nav.contact")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
